import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
    Row,
    Col,
    FormGroup,
    Label,
    Input,
    Button,
    Alert,
    Spinner
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { FaBarcode, FaCalendarAlt, FaWarehouse, FaBoxes } from 'react-icons/fa';
import { lotActions } from '../../sagas/lotSlice';
import { productActions } from '../../sagas/productSlice';

const LotForm = ({ lot, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { loading } = useSelector(state => state.lots);
    const {
        products,
        loading : productLoading,
        totalPages,
        totalElements,
        pageNumber,
        pageSize,
        error,
    } = useSelector((status) => status.products);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const [selectedArticle, setSelectedArticle] = useState(null);

    // Validation schema
    const validationSchema = Yup.object({
        numeroLot: Yup.string().required(t('lot.numeroLotRequired')),
        articleId: Yup.number().required(t('lot.articleRequired')),
        dateProduction: Yup.date().nullable(),
        dateExpiration: Yup.date()
            .nullable()
            .test('expiration-after-production', t('lot.expirationAfterProduction'), function (value) {
                const { dateProduction } = this.parent;
                if (!value || !dateProduction) return true;
                return new Date(value) > new Date(dateProduction);
            }),
        quantiteInitiale: Yup.number()
            .positive(t('lot.quantitePositive'))
            .required(t('lot.quantiteRequired')),
        quantiteDisponible: Yup.number()
            .min(0, t('lot.quantiteDisponibleMin'))
            .max(Yup.ref('quantiteInitiale'), t('lot.quantiteDisponibleMax'))
            .required(t('lot.quantiteDisponibleRequired')),
        emplacement: Yup.string().required(t('lot.emplacementRequired')),
        statut: Yup.string().required(t('lot.statutRequired'))
    });

    // Initial values
    const initialValues = {
        numeroLot: lot?.numeroLot || '',
        articleId: lot?.article?.id || lot?.articleId || '',
        dateProduction: lot?.dateProduction || '',
        dateExpiration: lot?.dateExpiration || '',
        quantiteInitiale: lot?.quantiteInitiale || 0,
        quantiteDisponible: lot?.quantiteDisponible || 0,
        emplacement: lot?.emplacement || '',
        statut: lot?.statut || 'ACTIF'
    };

    // Options pour les selects
    const statutOptions = [
        { value: 'ACTIF', label: t('lot.actif') },
        { value: 'EPUISE', label: t('lot.epuise') },
        { value: 'RETIRE', label: t('lot.retire') },
        { value: 'NON_CONFORME', label: t('lot.nonConforme') }
    ];

    const articleOptions = products?.map(article => ({
        value: article.id,
        label: `${article.code} - ${article.libelle}`,
        unite: article.unite
    })) || [];

    useEffect(() => {
        // Load products if not already loaded
        if (!products || products.length === 0) {
            dispatch(productActions.findAll({
                search: searchTerm,
                page: currentPage - 1,
                size: 10,
            }));
        }

        // Set selected article if editing
        if (lot && lot.article) {
            setSelectedArticle({
                value: lot.article.id,
                label: `${lot.article.code} - ${lot.article.libelle}`,
                unite: lot.article.unite
            });
        }
    }, [lot, products, dispatch]);

    // Generate automatic lot number
    const generateNumeroLot = async (articleId) => {
        if (articleId) {
            try {
                const response = await dispatch(lotActions.generateNumeroLotRequest({ articleId }));
                return response.payload.body;
            } catch (error) {
                console.error('Error generating lot number:', error);
                return '';
            }
        }
        return '';
    };

    // Handle form submission
    const handleSubmit = (values, { setSubmitting }) => {
        const payload = {
            data: {
                ...values,
                articleId: selectedArticle?.value
            },
            onSuccess: () => {
                Swal.fire({
                    icon: 'success',
                    title: t('common.success'),
                    text: lot ? t('lot.updateSuccess') : t('lot.createSuccess'),
                });
                onClose();
            },
            onError: (error) => {
                Swal.fire({
                    icon: 'error',
                    title: t('common.error'),
                    text: error.message || t('common.errorOccurred'),
                });
                setSubmitting(false);
            }
        };

        if (lot) {
            dispatch(lotActions.updateRequest({
                numeroLot: lot.numeroLot,
                ...payload
            }));
        } else {
            dispatch(lotActions.createRequest(payload));
        }
    };

    return (
        <div className="lot-form">
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, setFieldValue, isSubmitting, errors, touched }) => (
                    <Form>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="numeroLot">
                                        <FaBarcode className="me-1" />
                                        {t('lot.numeroLot')} *
                                    </Label>
                                    <div className="input-group">
                                        <Field
                                            name="numeroLot"
                                            type="text"
                                            className={`form-control ${errors.numeroLot && touched.numeroLot ? 'is-invalid' : ''}`}
                                            placeholder={t('lot.numeroLotPlaceholder')}
                                            readOnly={!!lot} // Read-only when editing
                                        />
                                        {!lot && (
                                            <Button
                                                type="button"
                                                color="outline-secondary"
                                                onClick={async () => {
                                                    if (selectedArticle) {
                                                        const numeroGenere = await generateNumeroLot(selectedArticle.value);
                                                        setFieldValue('numeroLot', numeroGenere);
                                                    } else {
                                                        Swal.fire({
                                                            icon: 'warning',
                                                            title: t('common.warning'),
                                                            text: t('lot.selectArticleFirst'),
                                                        });
                                                    }
                                                }}
                                            >
                                                {t('lot.generate')}
                                            </Button>
                                        )}
                                    </div>
                                    <ErrorMessage name="numeroLot" component="div" className="text-danger small mt-1" />
                                </FormGroup>
                            </Col>

                            <Col md={6}>
                                <FormGroup>
                                    <Label for="articleId">
                                        <FaBoxes className="me-1" />
                                        {t('lot.article')} *
                                    </Label>
                                    <Select
                                        options={articleOptions}
                                        value={selectedArticle}
                                        onChange={(option) => {
                                            setSelectedArticle(option);
                                            setFieldValue('articleId', option?.value || '');
                                        }}
                                        placeholder={t('lot.selectArticle')}
                                        isClearable
                                        isSearchable
                                        className={errors.articleId && touched.articleId ? 'is-invalid' : ''}
                                        isDisabled={!!lot} // Disabled when editing
                                    />
                                    <ErrorMessage name="articleId" component="div" className="text-danger small mt-1" />
                                    {selectedArticle && (
                                        <small className="text-muted">
                                            {t('lot.unite')}: {selectedArticle.unite}
                                        </small>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="dateProduction">
                                        <FaCalendarAlt className="me-1" />
                                        {t('lot.dateProduction')}
                                    </Label>
                                    <Field
                                        name="dateProduction"
                                        type="date"
                                        className={`form-control ${errors.dateProduction && touched.dateProduction ? 'is-invalid' : ''}`}
                                    />
                                    <ErrorMessage name="dateProduction" component="div" className="text-danger small mt-1" />
                                </FormGroup>
                            </Col>

                            <Col md={6}>
                                <FormGroup>
                                    <Label for="dateExpiration">
                                        <FaCalendarAlt className="me-1" />
                                        {t('lot.dateExpiration')}
                                    </Label>
                                    <Field
                                        name="dateExpiration"
                                        type="date"
                                        className={`form-control ${errors.dateExpiration && touched.dateExpiration ? 'is-invalid' : ''}`}
                                    />
                                    <ErrorMessage name="dateExpiration" component="div" className="text-danger small mt-1" />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label for="quantiteInitiale">
                                        {t('lot.quantiteInitiale')} *
                                    </Label>
                                    <div className="input-group">
                                        <Field
                                            name="quantiteInitiale"
                                            type="number"
                                            step="0.001"
                                            className={`form-control ${errors.quantiteInitiale && touched.quantiteInitiale ? 'is-invalid' : ''}`}
                                            placeholder="0.000"
                                            onChange={(e) => {
                                                const value = parseFloat(e.target.value) || 0;
                                                setFieldValue('quantiteInitiale', value);
                                                // Auto-set quantiteDisponible if it's a new lot
                                                if (!lot) {
                                                    setFieldValue('quantiteDisponible', value);
                                                }
                                            }}
                                        />
                                        <span className="input-group-text">
                                            {selectedArticle?.unite || 'U'}
                                        </span>
                                    </div>
                                    <ErrorMessage name="quantiteInitiale" component="div" className="text-danger small mt-1" />
                                </FormGroup>
                            </Col>

                            <Col md={4}>
                                <FormGroup>
                                    <Label for="quantiteDisponible">
                                        {t('lot.quantiteDisponible')} *
                                    </Label>
                                    <div className="input-group">
                                        <Field
                                            name="quantiteDisponible"
                                            type="number"
                                            step="0.001"
                                            className={`form-control ${errors.quantiteDisponible && touched.quantiteDisponible ? 'is-invalid' : ''}`}
                                            placeholder="0.000"
                                        />
                                        <span className="input-group-text">
                                            {selectedArticle?.unite || 'U'}
                                        </span>
                                    </div>
                                    <ErrorMessage name="quantiteDisponible" component="div" className="text-danger small mt-1" />
                                </FormGroup>
                            </Col>

                            <Col md={4}>
                                <FormGroup>
                                    <Label for="statut">
                                        {t('lot.statut')} *
                                    </Label>
                                    <Field
                                        as="select"
                                        name="statut"
                                        className={`form-control ${errors.statut && touched.statut ? 'is-invalid' : ''}`}
                                    >
                                        {statutOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="statut" component="div" className="text-danger small mt-1" />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label for="emplacement">
                                        <FaWarehouse className="me-1" />
                                        {t('lot.emplacement')} *
                                    </Label>
                                    <Field
                                        name="emplacement"
                                        type="text"
                                        className={`form-control ${errors.emplacement && touched.emplacement ? 'is-invalid' : ''}`}
                                        placeholder={t('lot.emplacementPlaceholder')}
                                    />
                                    <ErrorMessage name="emplacement" component="div" className="text-danger small mt-1" />
                                    <small className="text-muted">
                                        {t('lot.emplacementHelp')}
                                    </small>
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* Informations calculées */}
                        {values.quantiteInitiale > 0 && values.quantiteDisponible >= 0 && (
                            <Alert color="info" className="mt-3">
                                <Row>
                                    <Col md={4}>
                                        <strong>{t('lot.quantiteUtilisee')}:</strong>
                                        <div className="h5 text-primary">
                                            {(values.quantiteInitiale - values.quantiteDisponible).toFixed(3)} {selectedArticle?.unite || 'U'}
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <strong>{t('lot.pourcentageUtilise')}:</strong>
                                        <div className="h5 text-warning">
                                            {((values.quantiteInitiale - values.quantiteDisponible) / values.quantiteInitiale * 100).toFixed(1)}%
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <strong>{t('lot.pourcentageDisponible')}:</strong>
                                        <div className="h5 text-success">
                                            {(values.quantiteDisponible / values.quantiteInitiale * 100).toFixed(1)}%
                                        </div>
                                    </Col>
                                </Row>
                            </Alert>
                        )}

                        {/* Boutons d'action */}
                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <Button
                                type="button"
                                color="secondary"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                disabled={isSubmitting || !selectedArticle}
                            >
                                {isSubmitting && <Spinner size="sm" className="me-2" />}
                                {lot ? t('common.update') : t('common.create')}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default LotForm;
